import { createTheme } from "@mui/material/styles";
import { ReactComponent as ArrowHeadDown } from "@/assets/icons/Input/Select_Arrow_Head_Down.svg";

const shopeBorderRadius = 20;
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1920,
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#2A8AFD",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#FF414C",
    },
    error: {
      main: "#FF414C",
    },
  },
  shape: {
    borderRadius: shopeBorderRadius,
  },
  typography: {
    fontFamily: "Montserrat, -apple-system, BlinkMacSystemFont, Segoe UI",
    logo: {
      fontSize: "32px",
      fontWeight: "bold",
      lineHeight: "39px",
      margin: 0,
      textAlign: "center",
      color: "#04060F",
      whiteSpace: "nowrap",
    },
    navBarText: {
      fontSize: "17px",
      fontWeight: "400",
      lineHeight: "21px",
      margin: 0,
      textAlign: "center",
      color: "#04060F",
      whiteSpace: "nowrap",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
    h1: {
      fontSize: "40px",
      fontWeight: "bold",
      lineHeight: "49px",
      margin: 0,
      textAlign: "center",
      color: "#1761BA",
    },
    h2: {
      fontSize: "32px",
      fontWeight: "bold",
      lineHeight: "39px",
      margin: 0,
      textAlign: "center",
      color: "#1761BA",
    },
    h3: {
      fontSize: "30px",
      fontWeight: "bold",
      lineHeight: "37px",
      margin: 0,
      color: "#1761BA",
    },
    h5: {
      fontSize: "20px",
      fontWeight: "600",
      lineHeight: "28px",
      margin: 0,
      color: "#04060F",
    },
    body1: {
      fontSize: "20px",
      fontWeight: "400",
      lineHeight: "24px",
      margin: 0,
      color: "#04060F",
    },
    body2: {
      fontSize: "16px",
      fontWeight: "400",
      lineHeight: "20px",
      margin: 0,
      color: "#04060F",
    },
    body3: {
      fontSize: "18px",
      fontWeight: "400",
      lineHeight: "22px",
      margin: 0,
      color: "#04060F",
    },
    body4: {
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "17px",
      margin: 0,
      color: "#04060F",
    },
    body5: {
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "18px",
      margin: 0,
      color: "#04060F",
      width: "fit-content",
    },
    button: {
      fontSize: "18px",
      fontWeight: "600",
      lineHeight: "26px",
      margin: 0,
      color: "#fff",
      textTransform: "none",
      letterSpacing: "0.01em",
    },
  },
  colors: {
    darkFontColor: "#04060F",
    lightFontColor: "#fff",
    navbarBGColor: "#fff",
    white: "#fff",
    black: "#000",
    mainTheme: "#444",
    red: "#FF414C",
    green: "#1BC217",
    errorFontColor: "#d32f2f",
    blue: "#2A8AFD",
    blueDark: "#1761BA",
    unFocused: "#8A8A8B",
    noFound: "#ABB1BB",
    gray2: "#BEC6D6",
    gray3: "#444444",
  },
  filters: {
    blueDark:
      "invert(25%) sepia(30%) saturate(5950%) hue-rotate(203deg) brightness(90%) contrast(82%)",
    unFocused:
      "invert(62%) sepia(7%) saturate(40%) hue-rotate(202deg) brightness(88%) contrast(88%)",
  },
  gradients: {
    main: "linear-gradient(67.59deg, #2A8AFD 7.22%, #3FA1FC 114.24%)",
  },
  boxShadows: { main: "0px 4px 4px rgba(0, 0, 0, 0.15);" },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
      a {
        color: inherit;
        text-decoration: none;
      }

      ::-webkit-scrollbar {
        min-width: 4px;
        min-height: 4px;
        width: 1vw;
        height: 1vw;
        max-width: 6px;
        max-height: 6px;
      }
      ::-webkit-scrollbar-track {
        background-color: transparent;
      }
      ::-webkit-scrollbar-thumb {
        background-color: #2A8AFD;
        border-radius: 4px;
      }
      ::-webkit-scrollbar-thumb:hover {
        background-color: #3FA1FC;
      }
      scrollbar-color: #2A8AFD transparent;
    `,
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: "0",
          maxWidth: "1360px",
          [theme.breakpoints.up("lg")]: { maxWidth: "1360px", padding: "0" },
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          overflow: "visible",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableHead-root": {
            backgroundColor: "lightgray",
            border: "none",
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            background: "none",
            paddingTop: "0rem",
            paddingBottom: "32px",
            borderBottom: "1px solid #D1D9E8",
            fontSize: "20px",
            fontWeight: "700",
            lineHeight: "24px",
            color: "#04060F",
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root::first-of-type": {
            "& .MuiTableCell-root": {
              paddingTop: "32px",
            },
          },
          "& .MuiTableCell-root": {
            border: "none",
            paddingTop: "18px",
            paddingBottom: "18px",
            fontSize: "18px",
            fontWeight: "500",
            lineHeight: "22px",
            color: "#04060F",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          border: "none",
          whiteSpace: "nowrap",
          textAlign: "center",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: "white",
          "&:before": {
            content: "''",
            position: "absolute",
            zIndex: -1,
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            borderRadius: shopeBorderRadius,
            border: "1.5px solid transparent",
            background:
              "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.2) 51.56%, #FFFFFF 100%) border-box",
            WebkitMask:
              "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "destination-out",
            maskComposite: "exclude",
          },
          padding: "40px",
          "&.MuiPaper-root": {
            borderColor: "white",
            background: "rgba(255, 255, 255, 0.4)",
            backdropFilter: "blur(5px)",
            "-webkit-backdrop-filter": "blur(5px)",
            boxShadow: "none",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderColor: "white",
          background: "rgba(255, 255, 255, 0.4)",
          backdropFilter: "blur(5px)",
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        sizeLarge: {
          minWidth: "208px",
          fontSize: "18px",
          fontWeight: "600",
          lineHeight: "26px",
          minHeight: "55px",
        },
        sizeMedium: {
          minWidth: "192px",
          fontSize: "18px",
          fontWeight: "600",
          lineHeight: "26px",
          minHeight: "55px",
        },
        sizeSmall: {
          minWidth: "129.67px",
          fontSize: "16px",
          fontWeight: "400",
          lineHeight: "26px",
          minHeight: "37px",
        },
        root: ({ ownerState }) => ({
          ...(ownerState.variant === "contained" &&
            ownerState.color === "primary" && {
              background: "linear-gradient(45deg, #2A8AFD 30%, #3FA1FC 90%)",
              borderRadius: 20,
              color: "white",
              fontWeight: 600,
              "& .MuiCircularProgress-circle": {
                color: theme.colors.lightFontColor,
              },
            }),
          ...(ownerState.variant === "table" &&
            ownerState.color === "primary" && {
              background: "linear-gradient(45deg, #2A8AFD 30%, #3FA1FC 90%)",
              border: 0,
              borderRadius: 20,
              color: "white",
              boxShadow: "none",
              fontSize: "16px",
              fontWeight: "400",
              "& .MuiCircularProgress-circle": {
                color: theme.colors.lightFontColor,
              },
            }),
          ...(ownerState.variant === "outlined" &&
            ownerState.color === "primary" && {
              borderColor: "#2A8AFD",
              background: "rgba(255, 255, 255, 0.3)",
              backdropFilter: "blur(5px)",
              "& .MuiCircularProgress-circle": {
                color: "#2A8AFD",
              },
            }),
          ...(ownerState.variant === "outlined" &&
            ownerState.color === "error" && {
              borderColor: theme.palette.error.main,
              background: "rgba(255, 255, 255, 0.3)",
              backdropFilter: "blur(5px)",
              ":hover": {
                borderColor: theme.palette.error.main,
              },
              "& .MuiCircularProgress-circle": {
                color: theme.palette.error.main,
              },
            }),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#626262",
          fontSize: "18px",
          fontWeight: "400",
          lineHeight: "22px",
          marginBottom: "15px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          // marginTop: "14px",
          "& .MuiInputBase-root": {
            borderRadius: "40px",
          },
          "& .MuiInputBase-root.MuiInputBase-multiline": {
            padding: "0",
          },
          "& .MuiInputBase-input": {
            fontSize: "16px",
            fontWeight: "400",
            lineHeight: "19.5px",
            paddingTop: "19.5px",
            paddingBottom: "20.5px",
            paddingLeft: "27.62px",
            color: "#04060F !important",
          },
          "& textarea.MuiInputBase-inputMultiline": {
            height: "169px !important",
            boxSizing: "border-box",
            overflowY: "auto !important",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #BEC6D6 !important",
            paddingLeft: "26.5px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              border: "2px solid #BEC6D6 !important",
            },
          "&:hover .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
            {
              border: "1px solid #BEC6D6 !important",
            },
          "& .MuiInputBase-input::-webkit-outer-spin-button, & .MuiInputBase-input::-webkit-inner-spin-button":
            {
              WebkitAppearance: "none",
            },
          "& .MuiInputBase-input[type=number]": {
            WozAppearance: "textfield",
          },
          "& .MuiSelect-select": {
            textAlign: "left",
          },
          "& label.MuiFormLabel-root": {
            color: "#444",
            transform: "translate(33.58px, -9px) scale(0.75)",
          },
        },
      },
      defaultProps: { InputLabelProps: { shrink: true } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "40px",
          background: "rgba(255, 255, 255, 1)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          gap: "30px",
          textAlign: "center",
          "& .MuiPaper-root": {
            backgroundColor: "#fff",
            padding: "48.74px 41px 59.69px 42px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0px",
          },
          "& .MuiDialogContent-root": {
            padding: "20px",
          },
          "& .MuiDialogActions-root": {
            justifyContent: "center",
            gap: "27.44px",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          overflow: "hidden",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&::before, &::after": {
            opacity: "0",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minWidth: "155px",
          borderRadius: "40px",
          padding: "18px 8px 18px 27.62px",
          boxSizing: "border-box",
          fontSize: "16px",
          fontWeight: "500",
          lineHeight: "20px",
          color: "#04060F",
        },
        icon: {
          right: "19px",
          "&.Mui-disabled": {
            opacity: 0,
          },
        },
      },
      defaultProps: {
        IconComponent: ArrowHeadDown,
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          "& .MuiPaper-root": {
            marginTop: "7px",
            width: "auto",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(5px)",
            borderRadius: "40px",
            minWidth: "155px",
          },
          "& .MuiMenuItem-root": {
            height: "40px",
            fontSize: "16px",
            fontWeight: "500",
            lineHeight: "20px",
            paddingLeft: "30px",
            paddingRight: "30px",
          },
        },
        list: {
          paddingTop: "20px",
          paddingBottom: "20px",
        },
      },
    },
  },
  borderRadius: { main: "20px", large: "40px" },
  navbarHeight: "100px",
  footerHeight: "69px",
  border: { main: "1px solid #444" },
  gap: "30px",
});

export default theme;
